import React, { Component } from 'react';
import ReactTable from 'react-table';
import { Select, Icon, Input } from 'semantic-ui-react';
import { Field, change } from 'redux-form';
import * as _ from 'lodash';
import { USER_ACCESS_ROLE_FORM } from 'Common/FormEnum';
import { CheckboxField } from 'Common/SemanticWrapper';
import { caseInsensitiveFilter } from 'Common/GridHelper';
import { checkAllInGroup } from '../state/actions';
import { Preferences as PreferencesComponent } from './Preferences';
import { Checkbox } from './Checkbox';
import {
  extractSubMenuOptionsByRoot,
  groupDisplayReadOnly,
  groupDisplayPreferences,
  extractPreferences,
  CHECKBOX_TYPE_ALLOWREAD,
  CHECKBOX_TYPE_ALLOWWRITE,
  CHECKBOX_TYPE_PREFERENCES,
  accessRowToFieldId,
} from '../helpers/';

export class GroupFunctions extends Component {
  /**
   * Storing values in class, using state was causing
   * lagging redraw issues for the entire React Table.
   * Used when checkbox trigger form updates on
   * filtered list options.
   */
  filterFunctionName = null;
  filterSubMenuGroup = null;
  expanded = [];

  getFilteredData = () => {
    const { filterFunctionName, filterSubMenuGroup } = this;
    const { data } = this.props;
    let filteredData = data;

    if (filterFunctionName)
      filteredData = filteredData.filter(
        item => item.FunctionName.indexOf(filterFunctionName) > -1
      );

    // -1 is a flag for all submenus
    if (filterSubMenuGroup && filterSubMenuGroup !== -1)
      filteredData = filteredData.filter(item => {
        return (
          item.SubGroupName &&
          item.SubGroupName.indexOf(filterSubMenuGroup) > -1
        );
      });

    return filteredData;
  };

  getPreferences = () => extractPreferences(this.props.data);

  checkAllInFilteredGroup = (type, checked) => {
    const { dispatch, formValues, triggerReduxFormUpdate } = this.props;
    let filteredData = this.getFilteredData();

    if (type === CHECKBOX_TYPE_PREFERENCES)
      filteredData = extractPreferences(filteredData);

    dispatch(checkAllInGroup(filteredData, formValues, type, checked));

    triggerReduxFormUpdate();
  };

  checkPreferences = (preferences, checked) => {
    const { dispatch, formValues, triggerReduxFormUpdate } = this.props;
    dispatch(
      checkAllInGroup(
        preferences,
        formValues,
        CHECKBOX_TYPE_PREFERENCES,
        checked
      )
    );
    triggerReduxFormUpdate();
  };

  render() {
    const {
      data,
      title,
      formValues = {},
      dispatch,
      triggerReduxFormUpdate,
    } = this.props;

    return (
      <ReactTable
        data={_.sortBy(data, ['SubGroupName', 'Ordinal'])}
        filterable
        expanded={this.expanded}
        resizable={false}
        showPagination={false}
        pageSize={data.length}
        className="-striped -highlight"
        onExpandedChange={(newExpanded, index, event) => {
          this.expanded = newExpanded;
          triggerReduxFormUpdate();
        }}
        sortable={false}
        defaultFilterMethod={(filter, row) =>
          String(row[filter.id]) === filter.value
        }
        SubComponent={({ original: { Preferences = [], FunctionName } }) =>
          !!Preferences.length && (
            <PreferencesComponent
              data={Preferences}
              dispatch={dispatch}
              functionName={FunctionName}
            />
          )
        }
        columns={[
          {
            Header: `User Role Access for ${title} Pages`,
            columns: [
              {
                expander: true,
                Header: '',
                width: 40,
                Expander: ({ isExpanded, original: { Preferences = [] } }) => {
                  let props = {};

                  if (isExpanded) props = { rotated: 'clockwise' };

                  return (
                    !!Preferences.length && (
                      <Icon name="caret right" {...props} />
                    )
                  );
                },
              },
              /**
               * Function Name Column
               */
              {
                Header: 'Function Name',
                accessor: 'FunctionName',
                filterMethod: (filter, rows) =>
                  caseInsensitiveFilter(filter, rows),
                Filter: ({ filter, onChange }) => {
                  return (
                    <Input
                      onChange={(event, data) => {
                        event.preventDefault();
                        onChange(data.value);
                        this.filterFunctionName = data.value;
                      }}
                      placeholder="Filter by FunctionName"
                    />
                  );
                },
              },
              /**
               * SubGroup Name Column
               */
              {
                Header: 'SubGroup Name',
                accessor: 'SubGroupName',
                filterMethod: (filter, rows) => {
                  if (filter.value === -1) return rows;
                  return caseInsensitiveFilter(filter, rows);
                },
                Filter: ({ filter, onChange }) => {
                  const options = extractSubMenuOptionsByRoot(title, data);

                  if (!options.length) return null;

                  return (
                    <Select
                      onChange={(event, data) => {
                        onChange(data.value);
                        this.filterSubMenuGroup = data.value;
                      }}
                      placeholder="Filter by SubmenuGroup"
                      options={[
                        { key: '0', value: -1, text: 'All SubMenus' },
                        ...options,
                      ]}
                    />
                  );
                },
              },
              /**
               * Read Only Column
               */
              {
                Header: !groupDisplayReadOnly(data) ? '' : 'Read Only',
                id: 'readonly',
                accessor: '',
                className: 'column--checkbox',
                Cell: ({ original }) => {
                  const { IsViewOnlyApplicable } = original;
                  const Id = accessRowToFieldId(original);

                  return (
                    IsViewOnlyApplicable && (
                      <Field
                        component={CheckboxField}
                        name={`AllowRead${Id}`}
                        onChange={() => {
                          dispatch(
                            change(
                              USER_ACCESS_ROLE_FORM,
                              `AllowWrite${Id}`,
                              false
                            )
                          );
                        }}
                      />
                    )
                  );
                },
                maxWidth: 100,
                filterMethod: (filter, rows) => false,
                Filter: () => {
                  if (!groupDisplayReadOnly(data)) return null;

                  return (
                    <Checkbox
                      group={this.getFilteredData()}
                      formValues={formValues}
                      type={CHECKBOX_TYPE_ALLOWREAD}
                      onClick={(e, { checked }) => {
                        this.checkAllInFilteredGroup(
                          CHECKBOX_TYPE_ALLOWREAD,
                          checked
                        );
                      }}
                    />
                  );
                },
              },
              /**
               * Write Column
               */
              {
                Header: 'Write',
                id: 'write',
                accessor: '',
                className: 'column--checkbox',
                Cell: ({ original }) => {
                  const Id = accessRowToFieldId(original);
                  return (
                    <Field
                      component={CheckboxField}
                      name={`AllowWrite${Id}`}
                      onChange={() => {
                        dispatch(
                          change(USER_ACCESS_ROLE_FORM, `AllowRead${Id}`, false)
                        );
                      }}
                    />
                  );
                },
                maxWidth: 100,
                filterMethod: (filter, rows) => false,
                Filter: ({ filter, onChange }) => (
                  <Checkbox
                    group={this.getFilteredData()}
                    formValues={formValues}
                    type={CHECKBOX_TYPE_ALLOWWRITE}
                    onClick={(e, { checked }) => {
                      this.checkAllInFilteredGroup(
                        CHECKBOX_TYPE_ALLOWWRITE,
                        checked
                      );
                    }}
                  />
                ),
              },
              /**
               * Preferences Column
               */
              {
                Header: groupDisplayPreferences(data) ? 'Preferences' : '',
                id: 'preferences',
                accessor: '',
                className: 'column--checkbox',
                Cell: ({ original }) => {
                  return (
                    !!original.Preferences && (
                      <Checkbox
                        group={original.Preferences}
                        formValues={formValues}
                        type={CHECKBOX_TYPE_PREFERENCES}
                        onClick={(e, { checked }) => {
                          this.checkPreferences(original.Preferences, checked);
                        }}
                      />
                    )
                  );
                },
                maxWidth: 100,
                filterMethod: (filter, rows) => false,
                Filter: ({ filter, onChange, ...rest }) =>
                  groupDisplayPreferences(data) && (
                    <Checkbox
                      group={this.getPreferences()}
                      formValues={formValues}
                      type={CHECKBOX_TYPE_PREFERENCES}
                      onClick={(e, { checked }) => {
                        this.checkAllInFilteredGroup(
                          CHECKBOX_TYPE_PREFERENCES,
                          checked
                        );
                      }}
                    />
                  ),
              },
            ],
          },
        ]}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/userroleaccess/components/GroupFunctions.js